import i18n from '~/i18n'
import type { AppState } from '~/hooks/useAppStore'
import { ViewStateSchema } from '~/util/viewState'
import { z } from 'zod'
import { COSMOS_SITES, DEFAULT_MAX_ZOOM, type CosmosSite, type Dataset } from '~/constants'
import posthog from 'posthog-js'
import type { mapTooltipMachine } from '~/machines/mapTooltipMachine'
import type { ContextFrom } from 'xstate'

export const REGION_PARAM = 'region'
export type RegionSlug = CosmosSite | 'calwaves' | 'erosion'

export function getRegionFromURL(): RegionSlug | null {
  const raw = getURL().searchParams.get(REGION_PARAM)
  if (raw === 'calwaves') return 'calwaves'
  if (raw === 'erosion') return 'erosion'
  return (COSMOS_SITES as readonly string[]).includes(raw ?? '') ? (raw as CosmosSite) : null
}

export function setRegionInURL(region: RegionSlug) {
  const url = getURL()
  url.searchParams.set(REGION_PARAM, region)
  window.history.replaceState(null, '', url.toString())
}

/** Persistent address-bar slug: dataset for waves/erosion, site for Adaptation. */
export function regionSlugFor(dataset: Dataset, site: CosmosSite): RegionSlug {
  if (dataset === 'waves') return 'calwaves'
  if (dataset === 'erosion') return 'erosion'
  return site
}

const Params = {
  adaptation: z.string(),
  basemapType: z.union([z.literal('FLAT'), z.literal('SATELLITE')]),
  bathymetryVisible: z.boolean(),
  coordinates: z.object({ lng: z.number(), lat: z.number() }),
  dataset: z.union([z.literal('waves'), z.literal('cosmos'), z.literal('erosion')]),
  dem: z.string(),
  depths: z.array(z.number()),
  maxZoom: z.number(),
  overlayPanelVisible: z.boolean(),
  returnPeriod: z.string(),
  selectedGraphType: z.union([
    z.literal('reconstructed-time-series-3h'),
    z.literal('annual-wave-height-boxplot'),
    z.literal('wave-rose'),
    z.literal('hs-tp-density'),
    z.literal('hs-dir-density'),
    z.literal('extreme-value-analysis-gpd'),
  ]),
  sharedBy: z.string(),
  viewState: ViewStateSchema,
  wavesProperty: z.string(),
  wavePointName: z.string(),
  hsReturnPeriod: z.union([
    z.literal('2'),
    z.literal('5'),
    z.literal('10'),
    z.literal('20'),
    z.literal('50'),
    z.literal('100'),
  ]),
  hsYear: z.union([z.literal('2055'), z.literal('2085')]),
  language: z.union([z.literal('en'), z.literal('es')]),
}

type ParamsT = {
  [K in keyof typeof Params]: z.infer<(typeof Params)[K]>
}

function safeParse(obj: string | undefined | null) {
  if (!obj) {
    return undefined
  }
  try {
    return JSON.parse(obj)
  } catch (e) {
    console.debug(e)
  }
}

// We memoize the URL params here so that we can immediately clean the URL
// without losing the ability to retrieve the params multiple times.
let paramsMemo: ParamsT | undefined = undefined

export function getTrackedParamsFromURL(): ParamsT {
  if (paramsMemo) {
    return paramsMemo
  }

  const { searchParams: p } = getURL()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isZodObject(schema: z.ZodTypeAny): schema is z.ZodObject<any> {
    return schema._def.typeName === 'ZodObject'
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isZodArray(schema: z.ZodTypeAny): schema is z.ZodArray<any> {
    return schema._def.typeName === 'ZodArray'
  }

  function isZodBoolean(schema: z.ZodTypeAny): schema is z.ZodBoolean {
    return schema._def.typeName === 'ZodBoolean'
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isZodOptional(schema: z.ZodTypeAny): schema is z.ZodOptional<any> {
    return schema._def.typeName === 'ZodOptional'
  }

  function isOptionalArray(schema: z.ZodTypeAny): boolean {
    if (isZodOptional(schema)) {
      return isZodArray(schema._def.innerType)
    }
    return false
  }

  function parseFromZod(k: keyof typeof Params) {
    const schema = Params[k]
    const raw =
      isZodObject(schema) || isZodArray(schema) || isOptionalArray(schema) || isZodBoolean(schema)
        ? safeParse(p.get(k))
        : p.get(k)
    return schema.safeParse(raw).data ?? null
  }

  paramsMemo = Object.fromEntries(
    Object.keys(Params).map(function (k) {
      return [k, parseFromZod(k as keyof typeof Params)]
    })
  ) as ParamsT

  cleanURL()

  return paramsMemo
}

function cleanURL() {
  const url = getURL()
  const searchParams = url.searchParams
  let hasChanges = false
  Object.keys(Params).forEach((param) => {
    if (searchParams.has(param)) {
      searchParams.delete(param)
      hasChanges = true
    }
  })
  if (hasChanges) {
    url.search = searchParams.toString()
    const newUrl = url.toString()
    window.history.replaceState(null, '', newUrl)
  }
}

export function getURL(): URL {
  return new URL(window.location.href)
}

export function urlFromState(
  state: AppState,
  context: ContextFrom<typeof mapTooltipMachine>
): string {
  const {
    DEM: dem,
    adaptation,
    basemapType,
    dataset,
    depths,
    layerVisibility,
    maxZoom,
    overlayPanelVisible,
    returnPeriod,
    selectedGraphType,
    viewState,
    wavesProperty,
    hsReturnPeriod,
    hsYear,
  } = state

  const params: Record<keyof ParamsT, string | undefined> = {
    adaptation,
    basemapType,
    bathymetryVisible: String(layerVisibility.bathymetry),
    dataset,
    dem,
    depths: depths.length > 0 ? JSON.stringify(depths) : undefined,
    coordinates: context.coordinates ? JSON.stringify(context.coordinates) : undefined,
    maxZoom: (maxZoom !== DEFAULT_MAX_ZOOM ? maxZoom : undefined)?.toString(),
    overlayPanelVisible: String(overlayPanelVisible),
    returnPeriod,
    sharedBy: posthog.get_session_id(),
    viewState: viewState != null ? JSON.stringify(viewState) : undefined,
    wavePointName: context.wavePointName || undefined,
    wavesProperty,
    selectedGraphType: selectedGraphType as string,
    hsReturnPeriod,
    hsYear,
    language: i18n.language,
  }

  const newUrl = getURL()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      newUrl.searchParams.set(key, value)
    }
  })
  return newUrl.toString()
}
